import { useTranslations } from 'next-intl';

import { useEsiteCtx } from 'services/esite/context';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import useMyAccountLinks from 'components/notifications/hooks/use-my-account-links';

import { Anchor } from '../common';
import {
  PERSONAL_INFO_UPDATED,
  ADDRESS_BOOK_UPDATED,
  ADDRESS_BOOK_ADDED,
  ADDRESS_BOOK_DELETED,
  PAYMENT_METHOD_UPDATED,
  ABANDONED_CART,
  MY_EQUIPMENT_UPDATE,
  SHOPPING_PREFERENCES_UPDATED,
  ORDER_CREATED,
  ORDER_QUEUED,
  QUOTE_CREATED,
  QUOTE_EDITED,
  QUOTE_CONVERTED,
  QUOTE_DELETED
} from '../_constants';

function useCsrActions() {
  const t = useTranslations();
  const { catalogId, langId, storeId } = useEsiteCtx();
  const { staticLinks, dynamicLinks } = useMyAccountLinks(
    catalogId,
    storeId,
    langId
  );
  const { SHOPPING_CART, ORDER_DETAIL, PENDING_QUOTE_DETAIL } = dynamicLinks;

  const csrActions = {
    [PERSONAL_INFO_UPDATED]: () => {
      return replaceTokensWithComponents(
        t('PERSONAL_INFORMATION_HAS_BEEN_UPDATED', { '0': '{0}' }),
        <Anchor
          href={staticLinks.PERSONAL_INFO.getUrl()}
          text={String(t('PERSONAL_INFO')).toLocaleLowerCase()}
        />
      );
    },
    [ADDRESS_BOOK_UPDATED]: () => {
      return replaceTokensWithComponents(
        t('YOUR_ADDRESS_BOOK_HAS_BEEN_UPDATED', { '0': '{0}' }),
        <Anchor
          href={staticLinks.ADDRESS_BOOK.getUrl()}
          text={String(t('CSR_ADDRESS_BOOK')).toLocaleLowerCase()}
        />
      );
    },
    [ADDRESS_BOOK_ADDED]: () => {
      return replaceTokensWithComponents(
        t('YOUR_ADDRESS_BOOK_HAS_BEEN_ADDED', { '0': '{0}' }),
        <Anchor
          href={staticLinks.ADDRESS_BOOK.getUrl()}
          text={String(t('CSR_ADDRESS_BOOK')).toLocaleLowerCase()}
        />
      );
    },
    [ADDRESS_BOOK_DELETED]: () => {
      return replaceTokensWithComponents(
        t('YOUR_ADDRESS_BOOK_HAS_BEEN_DELETED', { '0': '{0}' }),
        <Anchor
          href={staticLinks.ADDRESS_BOOK.getUrl()}
          text={String(t('CSR_ADDRESS_BOOK')).toLocaleLowerCase()}
        />
      );
    },
    [PAYMENT_METHOD_UPDATED]: () => {
      return replaceTokensWithComponents(
        t('YOUR_PAYMENT_METHOD_HAS_BEEN_UPDATED', { '0': '{0}' }),
        <Anchor
          href={staticLinks.PAYMENT_METHODS.getUrl()}
          text={String(t('PAYMENT_METHOD')).toLocaleLowerCase()}
        />
      );
    },
    [ABANDONED_CART]: ({ orderId }) => {
      return replaceTokensWithComponents(
        t('YOUR_SHOPPING_CART_UPDATED', { '0': '{0}' }),
        <Anchor
          href={SHOPPING_CART.getUrl({ orderId })}
          text={String(t('SHOPPINGCART_TITLE')).toLocaleLowerCase()}
        />
      );
    },
    [SHOPPING_PREFERENCES_UPDATED]: () => {
      return replaceTokensWithComponents(
        t('YOUR_SHOPPING_PREFERENCES_HAS_BEEN_UPDATED', { '0': '{0}' }),
        <Anchor
          href={staticLinks.SHOPPING_PREFERENCES.getUrl()}
          text={String(t('CAT_SHOPPING_PREFERENCES_TITLE')).toLocaleLowerCase()}
        />
      );
    },
    [MY_EQUIPMENT_UPDATE]: () => {
      return replaceTokensWithComponents(
        t('YOUR_ASSOCIATED_EQUIPMENT_HAS_BEEN_UPDATED', { '0': '{0}' }),
        <Anchor
          href={staticLinks.MY_EQUIPMENT_UPDATE.getUrl()}
          text={String(t('ASSOCIATED_EQUIPMENT')).toLocaleLowerCase()}
        />
      );
    },
    [ORDER_CREATED]: ({ orderId, documentNumber }) => {
      const isPendingApproval = Boolean(!documentNumber && orderId);
      if (isPendingApproval) {
        return replaceTokensWithComponents(
          t('HAS_BEEN_PLACED_AND_PENDING_APPROVAL', { '0': '{0}' }),
          <Anchor
            href={ORDER_DETAIL.getUrl({ dbsOrderId: orderId, orderId })}
            text={`#${orderId}`}
          />
        );
      }

      return replaceTokensWithComponents(
        t('HAS_BEEN_PLACED', { '0': '{0}' }),
        <Anchor
          href={ORDER_DETAIL.getUrl({ dbsOrderId: documentNumber, orderId })}
          text={`#${documentNumber}`}
        />
      );
    },
    [ORDER_QUEUED]: ({ orderId }) => {
      return replaceTokensWithComponents(
        t('HAS_BEEN_QUEUED', { '0': '{0}' }),
        <Anchor
          href={ORDER_DETAIL.getUrl({ dbsOrderId: orderId, orderId })}
          text={`#${orderId}`}
        />
      );
    },
    [QUOTE_CREATED]: ({ quoteNumber, quoteId, expiresInDays }) => {
      if (!quoteNumber) return '';
      const message =
        +expiresInDays <= 0
          ? t('QUOTE_EXPIRATION_ACTION_BY_CSR', {
              '0': '{0}'
            })
          : t('QUOTE_CREATION_ACTION_BY_CSR', {
              '0': '{0}',
              '1': '{1}',
              '2': '{2}'
            });
      const day = String(
        t('QUOTE_EXPIRATION_DAY', { '0': '{0}' })
      ).toLocaleLowerCase();
      const days = String(t('FREQ_DAYS', { '0': '{0}' })).toLocaleLowerCase();
      const daysOrDayString = expiresInDays === 1 ? day : days;

      return replaceTokensWithComponents(
        message,
        <Anchor
          href={PENDING_QUOTE_DETAIL.getUrl({ orderId: quoteId })}
          text={`#${quoteNumber}`}
        />,
        expiresInDays,
        daysOrDayString
      );
    },
    [QUOTE_EDITED]: ({
      previousQuoteID,
      quoteId,
      previousQuoteNumber,
      quoteNumber
    }) => {
      const urlPreviousQuoteID = PENDING_QUOTE_DETAIL.getUrl({
        orderId: previousQuoteID
      });
      const linkQuoteID = PENDING_QUOTE_DETAIL.getUrl({
        orderId: quoteId
      });

      return replaceTokensWithComponents(
        t('QUOTE_EDITION_ACTION_BY_CSR', { '0': '{0}', '1': '{1}' }),
        <Anchor href={urlPreviousQuoteID} text={`#${previousQuoteNumber}`} />,
        <Anchor href={linkQuoteID} text={`#${quoteNumber}`} />
      );
    },
    [QUOTE_CONVERTED]: ({ quoteNumber, orderNumber, orderId }) => {
      if (!quoteNumber) return '';

      return replaceTokensWithComponents(
        t('QUOTE_CONVERSION_ACTION_BY_CSR', { '0': '{0}', '1': '{1}' }),
        `#${quoteNumber}`,
        <Anchor
          href={ORDER_DETAIL.getUrl({ dbsOrderId: orderNumber, orderId })}
          text={`#${orderNumber}`}
        />
      );
    },
    [QUOTE_DELETED]: ({ quoteNumber }) => {
      if (!quoteNumber) return '';

      return replaceTokensInString(
        t('QUOTE_DELETION_ACTION_BY_CSR', {
          0: `#${quoteNumber}`
        })
      );
    }
  };

  return { csrActions };
}

export default useCsrActions;
